/** Lander modules: `body.thrustRotate` (a Newtonian craft that rotates in
 * place and fires one main engine along its nose under constant gravity, no
 * drag, carried on top of that by the push protocol — a wind, a current) and
 * `rule.landingJudge` (first terrain contact is a landing only on a pad, on
 * both feet, inside a speed/tilt envelope — a looser one until the first
 * landing — else a crash). */
import { bool, id, int, num, obj, raisesSignal } from '../schemaBuilders.js';
import { defineModule, num as read, type ModuleCtx } from '../module.js';
import { clamp, heldBy, pushVelocity } from '../physics.js';
import type { EntityV1, PlayerInputV1 } from '../world.js';
import { heightAt, terrainOf, type TerrainPadV1, type TerrainV1 } from './terrain.js';

export type ThrustRotateConfig = {
  gravity: number;
  /** Main engine acceleration along the nose. */
  thrust: number;
  /** Attitude rate (radians per second) while left/right is held. */
  rotateSpeed: number;
  /** Largest attitude either way (radians). */
  maxTilt: number;
  /** Highest the craft's top may rise (y). */
  ceiling: number;
  /** Scalar the engine needs above zero to fire (a fuel meter's level). */
  fuelScalar?: string;
};

/** Ticks between two `ignite` cues, so a pulsed burn is not a buzz. */
const IGNITE_COOLDOWN_TICKS = 20;

/** Rotate a craft-local offset (x right, y down) by the clockwise attitude. */
export function craftPoint(e: EntityV1, lx: number, ly: number): { x: number; y: number } {
  const s = Math.sin(e.angle), c = Math.cos(e.angle);
  return { x: e.x + lx * c - ly * s, y: e.y - e.h / 2 + lx * s + ly * c };
}

const IDLE: PlayerInputV1 = { dx: 0, dy: 0, a: false, b: false, p: null };

export const thrustRotate = defineModule<ThrustRotateConfig>({
  id: 'body.thrustRotate', version: 1, attach: 'entity', role: 'movement',
  summary: 'Lander body: left/right rotates in place, the primary button fires one main engine along the nose (optionally needing a fuel scalar), constant gravity, no drag; walls and a ceiling stop it.',
  config: obj({ gravity: num(0, 60), thrust: num(0, 120), rotateSpeed: num(0.1, 12), maxTilt: num(0.1, 3.15), ceiling: num(-8, 64), fuelScalar: id() },
    ['fuelScalar']),
  requires: [], events: ['ignite'], presentation: { ignite: { cue: 'dash' } },
  proofs: ['falls-under-gravity', 'thrust-along-nose', 'rotation-free-and-clamped', 'no-fuel-no-thrust', 'thrust-signal-per-tick',
    'walls-and-ceiling', 'push-protocol', 'controller-lands-at-rest'],
  animStates: () => ['idle', 'move', 'action-1'],
  check: c => (c.thrust <= c.gravity ? ['the engine must out-push gravity'] : []),
  init(ctx, e) { Object.assign(ctx.data(e), { thrusting: false, turning: 0, cooldown: 0 }); },
  systems: {
    move(ctx, e) {
      if (!e) return;
      const c = ctx.config, d = ctx.data(e);
      if (read(d, 'cooldown') > 0) d.cooldown = read(d, 'cooldown') - 1;
      if (e.status !== 'active' || heldBy(e)) { d.thrusting = false; d.turning = 0; return; }
      const input = e.slot === null ? IDLE : ctx.input(e.slot);
      e.angle = clamp(e.angle + input.dx * c.rotateSpeed * ctx.dt, -c.maxTilt, c.maxTilt);
      const fuelled = c.fuelScalar === undefined || ctx.scalar(c.fuelScalar, 0) > 0;
      const thrusting = input.a && fuelled;
      const burn = thrusting ? c.thrust : 0;
      // Semi-implicit Euler: velocity first, then position (plus every push about it).
      e.vx += burn * Math.sin(e.angle) * ctx.dt;
      e.vy += (c.gravity - burn * Math.cos(e.angle)) * ctx.dt;
      const push = pushVelocity(ctx.signals('push'), e);
      e.x += (e.vx + push.vx) * ctx.dt; e.y += (e.vy + push.vy) * ctx.dt;
      const half = e.w / 2, w = ctx.world.bounds.w;
      if (e.x < half) { e.x = half; e.vx = Math.max(0, e.vx); }
      if (e.x > w - half) { e.x = w - half; e.vx = Math.min(0, e.vx); }
      if (e.y - e.h < c.ceiling) { e.y = c.ceiling + e.h; e.vy = Math.max(0, e.vy); }
      e.grounded = false; e.groundId = null;
      if (thrusting) {
        // The burn, every tick it lasts (a fuel meter spends on it).
        ctx.signal('thrust', e, e, { x: e.x, y: e.y, tags: [...e.tags] });
        if (d.thrusting !== true && read(d, 'cooldown') === 0) {
          ctx.emit('ignite', { x: e.x, y: e.y, slot: e.slot ?? 0 });
          d.cooldown = IGNITE_COOLDOWN_TICKS;
        }
      }
      d.thrusting = thrusting; d.turning = input.dx;
    },
    present(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      ctx.setAnim(e, d.thrusting === true ? 'move' : read(d, 'turning') !== 0 ? 'action-1' : 'idle');
    },
  },
  /** Fly to `goal` and arrive at rest: fall no faster than a stop over the
   * height left allows (at half the engine's spare push), hold height
   * while far off to the side, ease sideways in proportion to the offset,
   * and come in upright; point the nose along the push wanted and fire when
   * the nose carries at least half the engine's worth of it. */
  steer(ctx, e, goal) {
    const c = ctx.config;
    if (e.status !== 'active') return IDLE;
    const fuelled = c.fuelScalar === undefined || ctx.scalar(c.fuelScalar, 0) > 0;
    const dx = goal.x - e.x, h = goal.y - e.y, spare = c.thrust - c.gravity;
    const offset = Math.abs(dx);
    const clearance = offset < 0.25 ? 0 : Math.min(4, 2 * offset);
    const room = h - clearance;
    const vyWant = room <= 0 ? Math.max(-1, room) : 0.5 + Math.sqrt(0.9 * spare * room);
    const vxWant = clamp(0.8 * dx, h < 1 ? -0.5 : -1.6, h < 1 ? 0.5 : 1.6);
    const fx = (vxWant - e.vx) * 1.5, fy = (vyWant - e.vy) * 2.5 - c.gravity;
    const tiltLimit = Math.min(c.maxTilt, h < 1.2 ? 0.12 : 0.6);
    const want = -fy > 0.2 ? clamp(Math.atan2(fx, -fy), -tiltLimit, tiltLimit) : 0;
    const err = want - e.angle, turn = c.rotateSpeed * ctx.dt;
    const along = fx * Math.sin(e.angle) - fy * Math.cos(e.angle);
    return { dx: Math.abs(err) > turn / 2 ? (err > 0 ? 1 : -1) : 0, dy: 0, a: fuelled && along > 0.5 * c.thrust && Math.abs(err) < 0.5, b: false, p: null };
  },
});

export type LandingJudgeConfig = {
  /** The world.heightfield instance key whose rock and pads are judged. */
  terrain: string;
  /** Envelope: vertical and horizontal speed, and tilt (radians). */
  maxVy: number; maxVx: number; maxTilt: number;
  /** Until the first landing the envelope is this much looser, tilt capped. */
  grace: number; graceTiltCap: number;
  /** Footpads: half their span, and how far below the centre they hang. */
  feetHalf: number; feetDrop: number;
  /** Points = pad multiplier × (base + soft × softness). */
  basePoints: number; softPoints: number;
  /** Ticks resting on the pad before the goal is reported. */
  settleTicks: number;
  /** Signal reporting a banked landing's goal (a director's goal phase). */
  goalSignal: string;
  /** Ticks the craft is parked off the field before its next descent. */
  parkTicks: number;
  /** Show the HUD speed readouts flagged `!` while over the envelope. */
  flagOverLimit: boolean;
};

type JudgeCtx = ModuleCtx<LandingJudgeConfig>;

/** The envelope in force after `landed` banked landings. */
export function landingEnvelope(c: LandingJudgeConfig, landed: number): { vy: number; vx: number; tilt: number } {
  const g = landed <= 0 ? c.grace : 1;
  return { vy: c.maxVy * g, vx: c.maxVx * g, tilt: landed <= 0 ? Math.min(c.maxTilt * g, c.graceTiltCap) : c.maxTilt };
}

/** The landing pad both feet are over, if any. */
function padUnder(terrain: TerrainV1, left: number, right: number): TerrainPadV1 | undefined {
  return terrain.pads.find(p => left >= p.x0 && right <= p.x1);
}

function judge(ctx: JudgeCtx, e: EntityV1, terrain: TerrainV1): void {
  const c = ctx.config, d = ctx.data(e);
  const feet = [craftPoint(e, -c.feetHalf, c.feetDrop), craftPoint(e, c.feetHalf, c.feetDrop)];
  const hull = [craftPoint(e, -e.w / 2, -e.h / 2), craftPoint(e, e.w / 2, -e.h / 2), craftPoint(e, -e.w / 2, e.h / 2), craftPoint(e, e.w / 2, e.h / 2)];
  const down = [...feet, ...hull].filter(p => p.y >= heightAt(terrain, p.x));
  if (!down.length) return;
  const landed = read(d, 'landed');
  const limit = landingEnvelope(c, landed);
  const pad = padUnder(terrain, Math.min(feet[0]!.x, feet[1]!.x), Math.max(feet[0]!.x, feet[1]!.x));
  const at = { x: e.x, y: e.y - e.h / 2 };
  // Whatever touches must touch the deck: rock anywhere else breaks it.
  if (pad && down.every(p => p.x >= pad.x0 && p.x <= pad.x1) && Math.abs(e.vy) <= limit.vy && Math.abs(e.vx) <= limit.vx && Math.abs(e.angle) <= limit.tilt) {
    const softness = clamp(1 - Math.abs(e.vy) / limit.vy, 0, 1);
    const points = Math.round(pad.mult * (c.basePoints + c.softPoints * softness));
    e.angle = 0; e.y = pad.y - c.feetDrop + e.h / 2; e.vx = 0; e.vy = 0;
    e.status = 'frozen'; e.grounded = true; e.groundId = pad.entity;
    if (e.slot !== null) ctx.addScore(e.slot, points);
    Object.assign(d, { landed: landed + 1, settle: c.settleTicks });
    ctx.emit('land', { x: e.x, y: pad.y, slot: e.slot ?? 0, points, mult: pad.mult, soft: Math.round(softness * 100) });
    ctx.signal('landed', e, e, { x: e.x, y: pad.y, mult: pad.mult, tags: [...e.tags] });
    return;
  }
  ctx.emit('crash', { x: at.x, y: at.y, slot: e.slot ?? 0, onPad: pad !== undefined });
  ctx.signal('damage', e, null, { amount: 1, x: at.x, y: at.y, tags: [...e.tags] });
}

export const landingJudge = defineModule<LandingJudgeConfig>({
  id: 'rule.landingJudge', version: 1, attach: 'entity', role: 'rule', surfaceContact: true,
  summary: 'Landing judge: first terrain contact lands only with both feet on a pad inside a speed/tilt envelope (looser until the first landing) — scored by pad multiplier and softness, settled, then reported as a goal and parked for the next descent — else crashes (damage).',
  config: obj({
    terrain: id(), maxVy: num(0.1, 20), maxVx: num(0.1, 20), maxTilt: num(0.01, 1.5), grace: num(1, 3), graceTiltCap: num(0.01, 1.5),
    feetHalf: num(0.05, 4), feetDrop: num(0, 4), basePoints: int(0, 100000), softPoints: int(0, 100000), settleTicks: int(1, 300),
    goalSignal: raisesSignal(), parkTicks: int(1, 600), flagOverLimit: bool(),
  }),
  requires: ['world.heightfield', 'core.vitals'], events: ['land', 'crash'],
  presentation: { land: { cue: 'land', vfx: { effect: 'popup', text: 'LANDED' } }, crash: { cue: 'destroy', vfx: { effect: 'shake' } } },
  proofs: ['soft-upright-on-pad-lands', 'fast-crashes', 'tilted-crashes', 'off-pad-crashes', 'one-foot-off-pad-crashes', 'first-landing-grace',
    'scores-multiplier-by-softness', 'settles-then-reports-goal-and-parks'],
  animStates: () => ['win'],
  check(c, context) {
    const errors: string[] = [];
    if (!context.globals.includes('world.heightfield')) errors.push('a landing judge needs world.heightfield');
    if (c.graceTiltCap < c.maxTilt) errors.push('graceTiltCap must not tighten the tilt limit');
    return errors;
  },
  hud: () => [{ id: 'vs', label: 'VS', kind: 'text' }, { id: 'hs', label: 'HS', kind: 'text' }, { id: 'alt', label: 'ALT', kind: 'text' },
    { id: 'landed', label: 'LANDED', kind: 'counter' }],
  hudValues(ctx) {
    const e = ctx.entities({ status: 'any' }).find(x => x.slot === 0 && ctx.hasModule(x, 'rule.landingJudge'));
    const terrain = terrainOf(ctx.world, ctx.config.terrain);
    if (!e || !terrain) return { vs: '-', hs: '-', alt: '-', landed: 0 };
    const landed = read(ctx.data(e), 'landed');
    const limit = landingEnvelope(ctx.config, landed);
    const flag = (v: number, max: number) => v.toFixed(1) + (ctx.config.flagOverLimit && v > max ? '!' : '');
    const feet = e.y - e.h / 2 + ctx.config.feetDrop;
    return { vs: flag(Math.abs(e.vy), limit.vy), hs: flag(Math.abs(e.vx), limit.vx), alt: Math.max(0, heightAt(terrain, e.x) - feet).toFixed(1), landed };
  },
  init(ctx, e) { Object.assign(ctx.data(e), { landed: 0, settle: 0 }); },
  systems: {
    // Collide: after the body moved this tick, before vitals reads damage.
    collide(ctx, e) {
      if (!e) return;
      const d = ctx.data(e), c = ctx.config;
      if (read(d, 'settle') > 0) {
        d.settle = read(d, 'settle') - 1;
        if (read(d, 'settle') === 0) {
          ctx.signal(c.goalSignal, null, e, { landed: read(d, 'landed') });
          ctx.signal('park', e, null, { ticks: c.parkTicks });
        }
        return;
      }
      const terrain = terrainOf(ctx.world, c.terrain);
      // An invulnerable craft (vitals' respawn grace) is not judged.
      if (e.status === 'active' && !e.tags.includes('invulnerable') && terrain) judge(ctx, e, terrain);
    },
    present(ctx, e) {
      if (e && read(ctx.data(e), 'settle') > 0) ctx.setAnim(e, 'win');
    },
  },
  // Every pad a soft landing banks pulls; wider decks pull harder.
  solverTargets(ctx, e, player) {
    const terrain = terrainOf(ctx.world, ctx.config.terrain);
    if (!e || e.id !== player.id || e.status !== 'active' || !terrain) return [];
    return terrain.pads.map(p => ({ x: (p.x0 + p.x1) / 2, y: p.y, weight: padWeight(p), label: 'land' }));
  },
  // Landings banked: each outweighs the widest deck the world can hold.
  solverProgress(ctx, archetype) {
    if (archetype === null) return 0;
    const finished = 1 + padWeight({ x0: 0, x1: ctx.world.bounds.w });
    return ctx.entities({ archetype, status: 'any' }).reduce((n, e) => n + read(ctx.data(e), 'landed') * finished, 0);
  },
});

/** A pad's solver pull: wider decks are easier to bank. */
function padWeight(p: { x0: number; x1: number }): number {
  return 1 + (p.x1 - p.x0);
}
